<template>
  <div id="system-reports">
    <div class="dashboard-page-title">
      <div class="container-fluid">
        <h1>Reports</h1>
      </div>
    </div>

    <div class="container-fluid">

      <div class="dashboard-block">

        <h2>{{ $t('Requests') }} and Bookings</h2>

        <div class="row">
          <div class="col-lg-2">
            <h3>Data type:</h3>
          </div>
          <div class="col-lg-10">
            <div class="form-group">
              <selectize
                class="selectize selectize-single"
                name="data_type"
                :disable-delete="true"
                :options="reportDataTypeOptions"
                v-model="report.type"/>
            </div>
          </div>
        </div>

        <div class="row" v-if="selectedReport && selectedReport.passwordRequired">
          <div class="col-lg-2">
            <h3>Password:</h3>
          </div>
          <div class="col-lg-10">
            <div class="form-group">

              <input type="password"
                     class="form-control"
                     name="password"
                     v-model="report.password"
              />
            </div>
          </div>
        </div>

        <div class="row" v-if="selectedReport && selectedReport.datePicker">
          <div class="col-lg-2">
            <h3>Date range:</h3>
          </div>
          <div class="col-lg-8">
            <div class="form-group">
              <div class="input-group">
                <flatpickr
                  v-model="dateRange"
                  :config="flatPickerDateConfig"
                  ref="date"
                  class="form-control"
                  name="date_range"/>

                <span class="input-group-addon" data-toggle>
                  <i class="fa fa-calendar"></i>
                </span>

                <span class="input-group-addon" data-clear>
                  <i class="fa fa-times"></i>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-xs-12">
            <div class="form-group" v-if="$userIsGlobalAdmin && regionOptions.length > 1">
              <p>If you do not select any regions, the report will default to your region</p>
              <label class="sr-only">Region</label>
              <selectize
                ref="regions"
                class="selectize selectize-multiple"
                multiple
                name="region"
                v-model="report.selectedRegions"
                placeholder="Region"
                :options="regionOptions"/>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-2 pull-right">
            <ladda-button
              class="btn btn-block btn-dark"
              :click="handleRunReportCallback">
              Run
            </ladda-button>
          </div>
        </div>

        <div class="margin-20"></div>

        <div v-if="!$vlastMode">
          <ladda-button
            class="btn btn-block btn-dark"
            :click="handlePickFirstFile">
            Process First File
          </ladda-button>
          <input style="display: none;" ref="fileInput1" type="file" multiple @change="this.handleUploadFirstFile">

          <ladda-button
            class="btn btn-block btn-dark"
            :click="handlePickSecondFile">
            Process Second File
          </ladda-button>
          <input style="display: none;" ref="fileInput2" type="file" multiple @change="this.handleUploadSecondFile">
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { notify } from 'utils'
import Selectize from '../ext/codium/CodiumSelectize'
import Flatpickr from 'vue-flatpickr-component'
import { map } from 'lodash'
import { CommonTracker } from 'mixins/analytics'
export default {
  components: {
    Selectize,
    Flatpickr
  },
  mixins: [CommonTracker],
  data () {
    return {
      regionOptions: [],
      selectedDate: null,
      report: {
        type: null,
        password: null,
        from: null,
        to: null,
        selectedRegions: []
      },
      flatPickerDateConfig: {
        altInput: true,
        altFormat: 'd/m/Y',
        dateFormat: 'Y-m-d',
        wrap: true,
        mode: 'range'
      },
      errorMsg: ''
    }
  },

  computed: {
    ...mapState('clientConfig', ['client_config']),
    defaultReportDataTypeOptions () {
      return [
        { id: 'requests', text: this.$t('Requests'), datePicker: true },
        { id: 'drafts', text: 'Drafts', datePicker: true },
        this.$isACA ? { id: 'bookings_by_salesforce_id', text: 'Completed Bookings By Salesforce ID’s', datePicker: true } : { id: 'bookings', text: 'Completed Bookings', datePicker: true },
        { id: 'talent_bookings_duration_individual', text: 'Bookings - Individual ' + this.$options.filters.sentenceCase(this.$starLabel) + 's', datePicker: true },
        { id: 'talent_completed_bookings_and_duration_individual', text: 'Completed Bookings - Individual ' + this.$options.filters.sentenceCase(this.$starLabel) + 's', datePicker: true },
        { id: 'decline_reasons', text: this.$options.filters.sentenceCase(this.$starLabel) + ' Decline Reasons', datePicker: true },
        { id: 'activity', text: this.$options.filters.sentenceCase(this.$starLabel) + ' Activity', datePicker: true }
      ]
    },

    selectedReport () {
      return this.reportDataTypeOptions.find(element => element.id === this.report.type)
    },

    reportDataTypeOptions () {
      if (this.$vlastMode) {
        const departmentReport = this.$showDepartmentField ? [{ id: 'department_statistics', text: this.$t('Department') + ' statistics', datePicker: true }] : []
        const categoryReports = this.$categoryModuleEnabled
          ? [
            {
              id: 'budget_completed_per_talent_per_category_bi',
              text: (this.defaultCurrency === 'points' ? 'Points' : 'Budget') + ' completed per ' + this.$starLabel + ' per category',
              datePicker: true
            },
            {
              id: 'requests_completed_per_talent_per_category_bi',
              text: 'Requests completed per ' + this.$starLabel + ' per category',
              datePicker: true
            },
            {
              id: 'scheduled_n_completed_requests_per_talent_per_category_bi',
              text:  'Booked and Completed Requests per ' + this.$starLabel + ' per category',
              datePicker: true
            },
            {
              id: 'hours_completed_per_talent_per_category_bi',
              text: 'Hours completed per ' + this.$starLabel + ' per category',
              datePicker: true
            }
          ] : []
        const nflReport = this.$isNflClient ? [{ id: 'nfl_event_type_statistics', text: 'NFL event type statistics', datePicker: true }] : []
        const playerAppearances = this.$talentContractsModuleEnabled ? [{ id: 'player_appearances', text: this.$options.filters.sentenceCase(this.$starLabel) + ' Appearances', datePicker: true }] : []
        const fullList = this.userIsRestrictedAdminAndPermissionIsEnabled() ? [] : [{ id: 'full_admin_list', text: 'Full Admin List', passwordRequired: true }, { id: 'full_client_list', text: 'Full Client List', passwordRequired: true }, { id: 'full_manager_list', text: 'Full Manager List', passwordRequired: true }, { id: 'full_talent_list', text: 'Full Talent List', passwordRequired: true }]
        return [
          ...this.defaultReportDataTypeOptions,
          { id: 'self_verified', text: 'Self Verified', datePicker: false },
          { id: 'talent_completed_bookings_and_duration', text: this.$options.filters.sentenceCase(this.$starLabel) + ' Completed Bookings and Duration', datePicker: true },
          { id: 'hours_booked_per_talent_bi', text: 'Hours booked per ' + this.$starLabel, datePicker: true },
          { id: 'budget_completed_per_talent_bi', text: (this.defaultCurrency === 'points' ? 'Points' : 'Budget') + ' completed per ' + this.$starLabel, datePicker: true },
          ...categoryReports,
          { id: 'requests_completed_per_department_bi', text: this.$t('Requests') + ' completed per department', datePicker: true },
          { id: 'hours_completed_per_department_bi', text: 'Hours completed per department', datePicker: true },
          { id: 'pending_requests_by_player_bi', text: 'Pending ' + this.$t('requests') + ' per ' + this.$starLabel, datePicker: true },
          { id: 'booking_request_by_opportunity_type_bi', text: 'Appearances per ' + this.$t('request') + ' summary', datePicker: true },
          { id: 'talent_register_report', text: this.$options.filters.sentenceCase(this.$starLabel) + ' register report', datePicker: true },
          { id: 'register_talent_activity_report', text: 'Register ' + this.$starLabel + ' activity report', datePicker: true },
          // { id: 'full_client_list', text: 'Full Client List', passwordRequired: true },
          // { id: 'full_manager_list', text: 'Full Manager List', passwordRequired: true },
          // { id: 'full_talent_list', text: 'Full Talent List', passwordRequired: true },
          { id: 'expired_requests', text: 'Expired requests', datePicker: true },
          ...departmentReport,
          ...nflReport,
          ...playerAppearances,
          ...fullList
        ]
      } else {
        return [
          ...this.defaultReportDataTypeOptions,
          { id: 'marketing', text: 'Collected Emails', datePicker: true },
          { id: 'marketing_source', text: 'Marketing Source', datePicker: true },
          { id: 'booking_source', text: 'Admin Client Source', datePicker: false },
          { id: 'talent_app_activity', text: 'Star App Activity', datePicker: false },
          { id: 'talent_packages', text: 'Package ' + this.$options.filters.sentenceCase(this.$starLabelPlural), datePicker: false },
          { id: 'talent_social', text: this.$options.filters.sentenceCase(this.$starLabel) + ' Social Following', datePicker: false },
          { id: 'managed_talent', text: 'Managed ' + this.$options.filters.sentenceCase(this.$starLabelPlural), datePicker: false },
          { id: 'new_client', text: 'New Clients', datePicker: false },
          { id: 'response_time', text: 'Booking Response Times', datePicker: true },
          { id: 'talent_booking_time', text: 'Talent Booking Time', datePicker: false },
          { id: 'sales_pipeline', text: 'Sales Pipeline', datePicker: true },
          { id: 'withdrawn_declined', text: 'Withdrawn Declined', datePicker: true },
          { id: 'client_bookings_by_date', text: 'Clients who haven\'t booked since \'X\'', datePicker: true },
          { id: 'talent_budgets', text: 'Talent Budgets', datePicker: true }
        ]
      }
    },

    reportPrivateChannel () {
      return `${this.client_config.slug}.user.${this.$user.id}.reports`
    },

    dateRange: {
      get () {
        if (this.report.from === null || this.report.to === null) {
          return null
        }

        return [this.report.from, this.report.to]
      },

      set (value) {
        if (value.indexOf('to') !== -1) {
          [this.report.from, this.report.to] = value.split(' to ')
        }
      }
    }
  },

  created () {
    this.$echo.private(this.reportPrivateChannel)
      .listen('ReportGenerated', this.handleReportGeneratedCallback)
      .listen('ReportFailed', this.handleReportFailedCallback)
    if (this.$userIsGlobalAdmin && !this.$vlastMode) {
      this.getRegions()
        .then((response) => {
          this.regionOptions = map(response.data, region => ({id: region.id, text: region.name}))
          this.report.selectedRegions = [this.$user.region_id]
        })
    }
  },

  beforeDestroy () {
    this.$echo.leave(this.reportPrivateChannel)
  },

  methods: {
    ...mapActions('report', [
      'uploadTagsPart1',
      'uploadTagsPart2',
      'runReport'
    ]),

    ...mapActions('region', ['getRegions']),

    /**
     * Handle report generated notification.
     *
     * Triggers the download by assigning the download URL to the window.
     *
     * @return void
     */
    handleReportGeneratedCallback (event) {
      this.$http.get('reports/download', { params: { path: event.reportPathHash } }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data], {type: 'text/csv'}))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', event.reportNiceName + '.csv')
        document.body.appendChild(link)
        link.click()
      })
    },

    /**
     * Handle report failed notification.
     *
     * Shows an error message.
     *
     * @return void
     */
    handleReportFailedCallback (event) {
      notify.error('There was a problem generating a report. Please try again.')
    },

    /**
     * Notify end user that report is being generated.
     *
     * @return void
     */
    notifyReportBeingGenerated () {
      notify.success('Your report is being generated and will be downloaded shortly.')
    },

    notifyValidationError () {
      notify.error(this.errorMsg)
    },

    handlePickFirstFile () {
      this.$refs.fileInput1.value = null
      this.$refs.fileInput1.click()
    },

    handlePickSecondFile () {
      this.$refs.fileInput2.value = null
      this.$refs.fileInput2.click()
    },

    handleUploadFirstFile ({ target: { files } }) {
      return this.uploadTagsPart1(files[0])
    },

    handleUploadSecondFile ({ target: { files } }) {
      return this.uploadTagsPart2(files[0])
    },

    /**
     * Handle running a report.
     *
     * @return Promise
     */
    handleRunReportCallback () {
      if (!this.handleFieldValidation()) {
        document.querySelector('.ladda-button').disabled = false
        document.querySelector('.ladda-button').removeAttribute('data-loading')
        this.notifyValidationError()
        return Promise.resolve()
      } else {
        this.trackEvent(this.analytics.V2.ReportGenerateEvent, [this.$options.filters.titleCase(this.report.type)])
        return this.runReport({
          report: this.report.type,
          payload: this.report,
          passwordProtected: this.selectedReport.passwordRequired
        }).then(this.notifyReportBeingGenerated)
          .catch((e) => {
            return Promise.resolve()
          })
      }
    },

    handleFieldValidation () {
      if (this.report.type === null) {
        this.errorMsg = 'Data type is a required field.'
        return false
      }
      return true
    },

    userIsRestrictedAdminAndPermissionIsEnabled () {
      return this.$user.role === 'restricted-admin' && this.$restrictedAdminPermission
    }
  }
}
</script>

<style lang="scss">
#system-reports {
  .selectize-input {
    border: 2px solid black !important;
  }
}
</style>
